import * as React from "react";
import {
  Typography,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer
} from "../../components/mui";
import { AddRoundedIcon, AssignmentTurnedInRoundedIcon, DeleteForeverRoundedIcon, EditRoundedIcon, ChevronLeftRoundedIcon, CheckCircleRoundedIcon } from "../../components/icons";
import Modal from "../../components/shared/Modal";
import ConfirmDialog from "../../components/shared/ConfirmDialog";
import { useToast } from "../../components/shared/ToastProvider";
import { runFieldValidators, requiredField, positiveNumberField } from "../../utils/validation";

import "../../styles/main.css";
import "../../styles/pages/submitRf.css";
import PageLayout from "../../components/shared/PageLayout";

// Hardcoded data - Backend developer will replace with API calls
const CONDO_UNITS = [
  "141 Apartment - 15C 141 Apartment",
  "Island Plaza - 8A Island Plaza",
  "Jazz Residences - 22F Jazz Residences"
];

const PARTICULAR_TYPES = ["Rental", "Allowance", "Misc Expense", "Utilities"];

// Hardcoded RF data for editing
const MOCK_RF_DATA = {
  rfNo: "12035",
  subject: "2368 Mabolo St Dasmarinas Village Internet",
  paymentTerm: "Cash",
  currency: "PHP",
  remarks: "Monthly internet bill payment",
  vendor: "PLDT",
  particulars: [
    {
      id: 1,
      type: "Utilities",
      condoUnit: "Island Plaza - 8A Island Plaza",
      securityDeposit: "No",
      qty: 1,
      unitCost: 2699.00,
      total: 2699.00,
      periodFrom: "2026-01-09",
      periodTo: "2026-02-08"
    }
  ]
};

const formatParticularDescription = (item) => {
  const periodLabel = item.periodFrom && item.periodTo ? ` (${item.periodFrom} – ${item.periodTo})` : "";
  return `${item.condoUnit} - ${item.type}${periodLabel}`;
};

export default function EditRf({ onLogout, onNavigate, rfNo }) {
  const [subject, setSubject] = React.useState("");
  const [paymentTerm, setPaymentTerm] = React.useState("Cash");
  const [currency, setCurrency] = React.useState("PHP");
  const [remarks, setRemarks] = React.useState("");
  const [vendor, setVendor] = React.useState("");
  const [particulars, setParticulars] = React.useState([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [subjectError, setSubjectError] = React.useState("");
  const [formValues, setFormValues] = React.useState({
    type: PARTICULAR_TYPES[0],
    condoUnit: CONDO_UNITS[0],
    securityDeposit: "No",
    quantity: "1",
    unitCost: "0",
    periodFrom: "",
    periodTo: ""
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [rowPendingDelete, setRowPendingDelete] = React.useState(null);
  const { showToast } = useToast();

  // Load RF data on mount
  React.useEffect(() => {
    // Backend developer will fetch data by rfNo from API
    const rfData = MOCK_RF_DATA;
    setSubject(rfData.subject);
    setPaymentTerm(rfData.paymentTerm);
    setCurrency(rfData.currency);
    setRemarks(rfData.remarks);
    setVendor(rfData.vendor);
    setParticulars(rfData.particulars);
  }, [rfNo]);

  const handleAddParticular = () => {
    setEditingId(null);
    setFormValues({
      type: PARTICULAR_TYPES[0],
      condoUnit: CONDO_UNITS[0],
      securityDeposit: "No",
      quantity: "1",
      unitCost: "0",
      periodFrom: "",
      periodTo: ""
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleEditParticular = (item) => {
    setEditingId(item.id);
    setFormValues({
      type: item.type,
      condoUnit: item.condoUnit,
      securityDeposit: item.securityDeposit || "No",
      quantity: String(item.qty),
      unitCost: String(item.unitCost),
      periodFrom: item.periodFrom,
      periodTo: item.periodTo
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleDeleteRequest = (item) => {
    setRowPendingDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteParticular = () => {
    if (rowPendingDelete) {
      setParticulars((prev) => prev.filter((item) => item.id !== rowPendingDelete.id));
    }
    setDeleteDialogOpen(false);
    setRowPendingDelete(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRowPendingDelete(null);
  };

  const handleFormValueChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveParticular = () => {
    const validators = [
      { field: "type", label: "Type", validator: requiredField("{label}") },
      { field: "condoUnit", label: "Condo Unit", validator: requiredField("{label}") },
      { field: "periodFrom", label: "Period From", validator: requiredField("{label}") },
      { field: "periodTo", label: "Period To", validator: requiredField("{label}") },
      { field: "quantity", label: "Quantity", validator: positiveNumberField("{label}") },
      { field: "unitCost", label: "Unit Cost", validator: positiveNumberField("{label}") }
    ];

    const errors = runFieldValidators(validators, formValues);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const qty = parseFloat(formValues.quantity) || 0;
    const unitCost = parseFloat(formValues.unitCost) || 0;

    const row = {
      id: editingId ?? Date.now(),
      type: formValues.type,
      condoUnit: formValues.condoUnit,
      securityDeposit: formValues.securityDeposit,
      qty,
      unitCost,
      total: qty * unitCost,
      periodFrom: formValues.periodFrom,
      periodTo: formValues.periodTo
    };

    setParticulars((prev) => {
      if (editingId) {
        return prev.map((item) => (item.id === editingId ? row : item));
      }
      return [...prev, row];
    });

    setModalOpen(false);
    setEditingId(null);
  };

  const calculateGrandTotal = () => {
    return particulars.reduce((sum, item) => sum + (item.total || 0), 0).toFixed(2);
  };

  const handleSubmit = () => {
    const subjectValidation = requiredField("Subject")(subject);
    if (subjectValidation) {
      setSubjectError(subjectValidation);
      return;
    }
    setSubjectError("");

    console.log("Updating RF:", {
      rfNo: MOCK_RF_DATA.rfNo,
      subject,
      paymentTerm,
      currency,
      remarks,
      particulars,
      vendor,
      total: calculateGrandTotal()
    });
    // Backend developer will implement API call here
    showToast({ message: "Request updated successfully", severity: "success" });
  };

  const handleBack = () => {
    onNavigate({ key: "rf-history" });
  };

  return (
    <PageLayout 
      activeKey="rf-history" 
      onLogout={onLogout} 
      onNavigate={onNavigate}
      className="page-content submit-rf-page"
    >
      {/* RF Form */}
      <Box className="content-box submit-rf-form">
        {/* Header */}
        <Box className="submit-rf-header">
          <Box className="submit-rf-header-icon">
            <AssignmentTurnedInRoundedIcon />
          </Box>

        {/* Modal */}
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingId ? "Edit Particular" : "Create Particular"}
          actions={(
            <>
              <Button variant="outlined" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveParticular}>
                {editingId ? "Update" : "Create"}
              </Button>
            </>
          )}
          maxWidth="sm"
          fullWidth
        >
          <Box className="modal-form-grid modal-form-grid--spaced submit-rf-modal-grid">
            <Box className="modal-form-row">
              <FormControl fullWidth size="small" required error={!!formErrors.type}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formValues.type}
                  label="Type"
                  onChange={(e) => handleFormValueChange("type", e.target.value)}
                >
                  {PARTICULAR_TYPES.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.type && <FormHelperText>{formErrors.type}</FormHelperText>}
              </FormControl>
            </Box>

            <Box className="modal-form-row">
              <FormControl fullWidth size="small" required error={!!formErrors.condoUnit}>
                <InputLabel>Condo Unit</InputLabel>
                <Select
                  value={formValues.condoUnit}
                  label="Condo Unit"
                  onChange={(e) => handleFormValueChange("condoUnit", e.target.value)}
                >
                  {CONDO_UNITS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.condoUnit && <FormHelperText>{formErrors.condoUnit}</FormHelperText>}
              </FormControl>
            </Box>

            <Box className="modal-form-row" display="flex" gap={1}>
              <FormControl fullWidth size="small" required error={!!formErrors.securityDeposit}>
                <InputLabel>Security Deposit</InputLabel>
                <Select
                  value={formValues.securityDeposit}
                  label="Security Deposit"
                  onChange={(e) => handleFormValueChange("securityDeposit", e.target.value)}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
                {formErrors.securityDeposit && <FormHelperText>{formErrors.securityDeposit}</FormHelperText>}
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Quantity"
                type="number"
                required
                value={formValues.quantity}
                onChange={(e) => handleFormValueChange("quantity", e.target.value)}
                error={!!formErrors.quantity}
                helperText={formErrors.quantity}
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Unit Cost"
                type="number"
                required
                value={formValues.unitCost}
                onChange={(e) => handleFormValueChange("unitCost", e.target.value)}
                error={!!formErrors.unitCost}
                helperText={formErrors.unitCost}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>

            <Box className="modal-form-row" display="flex" gap={1}>
              <TextField
                fullWidth
                size="small"
                label="Period From"
                type="date"
                InputLabelProps={{ shrink: true }}
                required
                value={formValues.periodFrom}
                onChange={(e) => handleFormValueChange("periodFrom", e.target.value)}
                error={!!formErrors.periodFrom}
                helperText={formErrors.periodFrom}
              />
              <TextField
                fullWidth
                size="small"
                label="Period To"
                type="date"
                InputLabelProps={{ shrink: true }}
                required
                value={formValues.periodTo}
                onChange={(e) => handleFormValueChange("periodTo", e.target.value)}
                error={!!formErrors.periodTo}
                helperText={formErrors.periodTo}
              />
            </Box>
          </Box>
        </Modal>

        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Particular"
          message={rowPendingDelete ? `Remove ${formatParticularDescription(rowPendingDelete)} from the list?` : "Remove this particular?"}
          confirmLabel="Delete"
          onConfirm={handleDeleteParticular}
          onClose={handleCloseDeleteDialog}
        />
          <Box className="submit-rf-header-content">
            <Typography variant="h5" className="submit-rf-header-title">
              Edit Condo Request Form
            </Typography>
            <Typography variant="body2" className="submit-rf-header-description">
              Update requisition form details for condo-related expenses and purchases.
            </Typography>
          </Box>
        </Box>

        {/* Subject */}
        <Box className="submit-rf-field">
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!subjectError}
            helperText={subjectError}
          />
        </Box>

        {/* Payment Term & Currency */}
        <Box className="submit-rf-row">
          <FormControl fullWidth size="small">
            <InputLabel>Payment Term</InputLabel>
            <Select
              value={paymentTerm}
              onChange={(e) => setPaymentTerm(e.target.value)}
              label="Payment Term"
            >
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Check">Check</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
              <MenuItem value="Credit Card">Credit Card</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" className="currency-readonly">
            <InputLabel>Currency</InputLabel>
            <Select
              value={currency}
              label="Currency"
              readOnly
              inputProps={{ readOnly: true }}
            >
              <MenuItem value="PHP">PHP</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Remarks */}
        <Box className="submit-rf-field">
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter remarks"
          />
        </Box>

        {/* Particulars Table + Actions */}
        <Box className="submit-rf-particulars">
          <Box className="submit-rf-particulars-header">
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={handleAddParticular}
              size="small"
              className="submit-rf-add-button"
            >
              Add Particular
            </Button>
          </Box>

          <TableContainer className="data-table-wrapper">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Unit Cost</TableCell>
                  <TableCell>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {particulars.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="submit-rf-empty-cell">
                      <Typography variant="body2" color="text.secondary" className="submit-rf-empty-text">
                        No particulars added yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  particulars.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" className="submit-rf-table-primary">
                          {formatParticularDescription(item)}
                        </Typography>
                        <Box className="submit-rf-particular-actions">
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<EditRoundedIcon fontSize="small" />}
                            onClick={() => handleEditParticular(item)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            color="primary"
                            startIcon={<DeleteForeverRoundedIcon fontSize="small" />}
                            onClick={() => handleDeleteRequest(item)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                      <TableCell>{item.qty}</TableCell>
                      <TableCell>{item.unitCost.toFixed(2)}</TableCell>
                      <TableCell className="submit-rf-total">{item.total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}

                {/* Total Row */}
                {particulars.length > 0 && (
                  <TableRow className="submit-rf-total-row">
                    <TableCell colSpan={3}>
                      <Typography variant="body2" className="submit-rf-total-label">
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" className="submit-rf-grand-total">
                        {calculateGrandTotal()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Vendor */}
        <Box className="submit-rf-field">
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Vendor"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder="Enter vendor details"
          />
        </Box>

        {/* Action Buttons */}
        <Box className="submit-rf-actions">
          <Button
            variant="outlined"
            startIcon={<ChevronLeftRoundedIcon />}
            onClick={handleBack}
            size="large"
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircleRoundedIcon />}
            onClick={handleSubmit}
            size="large"
          >
            Submit Request
          </Button>
        </Box>
      </Box>
    </PageLayout>
  );
}
